// 표 머리를 눌러 정렬하기 — 누를 때마다 한 단계씩(2026-10-01 오너 요청, 고객사 마스터).
//
//   글자(상호·위치·렌탈사)      1번: 가나다순   2번: 역순       3번: 해제
//   숫자(매장 수·계약기간·대수·금액) 1번: 작은 순    2번: 큰 순      3번: 해제
//   날짜(계약일·도입일·반납일)   1번: 최신       2번: 과거       3번: 해제
//   임박(D-Day·회수일)         1번: 임박순     2번: 역순       3번: 해제
//   분류(진행 상태)            1번: 분류별     2번: 해제
//   O/X(보증 보험)             1번: O 먼저     2번: X 먼저     3번: 해제
//
// 다른 칸을 누르면 그 칸의 1번부터 시작한다(정렬은 한 칸씩만).
// **빈 값은 어느 방향이든 맨 뒤**다 — 역순으로 바꿨다고 빈 칸이 맨 위로 올라오면 정작 보려던
// 값이 밀려난다. 같은 값끼리는 원래 순서(서버 순서)를 지킨다(Array.prototype.sort 는 안정 정렬).

export type ColumnSortKind = 'text' | 'number' | 'date' | 'imminent' | 'category' | 'flag';

export interface ColumnSortState<K extends string = string> {
  key: K;
  /** 1 = 첫 번째 누름의 방향, 2 = 두 번째 누름(역방향). 분류는 1만 있다. */
  step: 1 | 2;
}

/** 머리를 한 번 눌렀을 때의 다음 상태. null = 정렬 해제. */
export function nextColumnSort<K extends string>(
  current: ColumnSortState<K> | null,
  key: K,
  kind: ColumnSortKind,
): ColumnSortState<K> | null {
  if (!current || current.key !== key) return { key, step: 1 };
  if (current.step === 1 && kind !== 'category') return { key, step: 2 };
  return null;
}

/** 지금 상태가 무엇인지, 다음에 누르면 무엇이 되는지 — 머리의 안내 글자. */
export function columnSortHint(kind: ColumnSortKind, step: 1 | 2 | null): string {
  const steps: Record<ColumnSortKind, string[]> = {
    text: ['가나다순', '역순'],
    number: ['작은 순', '큰 순'],
    date: ['최신순', '과거순'],
    imminent: ['임박순', '역순'],
    category: ['진행 상태별'],
    flag: ['O 먼저', 'X 먼저'],
  };
  const s = steps[kind];
  const now = step === null ? '정렬 안 함' : s[step - 1];
  const next = step === null ? s[0] : step < s.length ? s[step] : '정렬 해제';
  return `지금은 ${now}, 누르면 ${next}`;
}

/**
 * 비교에 쓰는 값.
 *   text: 글자 · number: 수 · date: 시각(ms) · imminent: 오늘부터 남은 날(지난 날은 음수)
 *   category: 분류 순번(작을수록 앞) · flag: O = 0, X = 1(모르면 빈 값)
 * 값이 없으면 null(빈 문자열도 글자에서는 빈 값으로 본다).
 */
export type ColumnSortValue = string | number | null | undefined;

function isBlank(v: ColumnSortValue): v is null | undefined | '' {
  return v === null || v === undefined || (typeof v === 'string' && v.trim() === '');
}

/**
 * 임박순의 순번 — 오늘·앞으로 올 날이 가까운 것부터, 그다음 이미 지난 날이 최근인 것부터.
 * (D-3 → D-10 → D-90 → D+1 → D+30). 지난 것을 맨 위에 두면 '곧 닥칠 일'을 보려던 화면이
 * 오래전에 끝난 일로 덮인다.
 */
function imminentRank(days: number): [number, number] {
  return days >= 0 ? [0, days] : [1, -days];
}

/** 두 값 비교(빈 값은 늘 뒤). step 2 는 1의 정확한 역순이다(빈 값만 그대로 뒤). */
export function compareColumnValues(
  kind: ColumnSortKind,
  a: ColumnSortValue,
  b: ColumnSortValue,
  step: 1 | 2,
): number {
  const blankA = isBlank(a);
  const blankB = isBlank(b);
  if (blankA || blankB) return blankA === blankB ? 0 : blankA ? 1 : -1;
  let c: number;
  if (kind === 'text') {
    c = String(a).localeCompare(String(b), 'ko', { numeric: true });
  } else if (kind === 'imminent') {
    const [ga, da] = imminentRank(Number(a));
    const [gb, db] = imminentRank(Number(b));
    c = ga !== gb ? ga - gb : da - db;
  } else if (kind === 'date') {
    // 날짜의 첫 누름은 '최신'(내림차순).
    c = Number(b) - Number(a);
  } else {
    c = Number(a) - Number(b);
  }
  return step === 1 ? c : -c;
}

/** 행 배열을 한 칸 기준으로 정렬한 **새 배열**(원본은 그대로). */
export function sortRowsByColumn<T>(
  rows: readonly T[],
  kind: ColumnSortKind,
  valueOf: (row: T) => ColumnSortValue,
  step: 1 | 2,
): T[] {
  const keyed = rows.map((row) => ({ row, v: valueOf(row) }));
  keyed.sort((x, y) => compareColumnValues(kind, x.v, y.v, step));
  return keyed.map((k) => k.row);
}
